import { useRef, useState, useMemo, useEffect } from 'react';
import { Tabs, Tab, Pagination, Modal, ModalContent, ModalHeader, ModalBody, useDisclosure } from "@nextui-org/react"; 
import { MapPin, Phone } from 'lucide-react';
import {
    APIProvider,
    Map,
    Marker,
  } from '@vis.gl/react-google-maps';

import { calculateMapCenterFromMarkers, formatPhoneNumber, formatLanguages } from '../../lib/utils';
import Text from '../ui/text';
import { t } from 'i18next';

const ProviderList = props => {
    const containerRef = useRef();
    const { requestParams, listDataIsPending = false, providers = [], total, page, setPage, setProviderId, errors = [] } = props;
    const [selectedTab, setSelectedTab] = useState("list");
    const {isOpen, onOpen, onOpenChange} = useDisclosure();
    const [selectedMarker, setSelectedMarker] = useState();

    const { center: mapCenter, zoom: mapZoom } = useMemo(() => calculateMapCenterFromMarkers(providers), [providers, page]);
    const markers = useMemo(() => {
        if (!providers || providers?.length === 0) return [];
        const markersByCoord = {};
        providers.forEach(item => {
            const latLngKey = `${item.latitude},${item.longitude}`;
            if (!markersByCoord[latLngKey]) {
                markersByCoord[latLngKey] = {
                    key: latLngKey,
                    lat: item.latitude,
                    lng: item.longitude,
                    name: item.providerName,
                    items: [item],
                };
            } else {
                markersByCoord[latLngKey].items.push(item);
            }
        });
        return Object.values(markersByCoord);
    }, [providers]);

    useEffect(() => { if(selectedMarker) onOpen(); }, [selectedMarker]);
    useEffect(() => {
        if (providers?.length > 0) {
            containerRef.current.scrollIntoView({ behavior: "instant", block: "nearest", inline: "nearest" });
        } 
    }, [providers, selectedTab])


    return (
        <div ref={containerRef} >
            {total > 0 && (
                <div className='w-full flex flex-col justify-center sm:flex-row sm:justify-between gap-3'>
                    <div className='flex-1 flex flex-row justify-start'>
                        <Tabs 
                            className='w-full' 
                            variant='underlined' 
                            aria-label="By Tab"
                            selectedKey={selectedTab}
                            onSelectionChange={setSelectedTab}
                            classNames={{
                                tabList: "gap-0 w-full relative rounded-none",
                                cursor: "w-full bg-gray-600",
                                tab: "mx-0 bg-transparent data-[focus=true]:outline-0 data-[selected=true]:outline-0 outline-0 flex-1 px-0 h-12 justify-start text-base hover:border-0 hover:border-b border-0 border-b hover:border-gray-200 border-gray-200",
                                tabContent: "bg-transparent font-semibold text-gray-500 hover:text-gray-500 group-data-[selected=true]:outline-0 group-data-[selected=true]:text-gray-600 pl-2"
                            }}
                        >
                            <Tab key="list" title="By List" />
                            <Tab key="map" title="By Map"/>
                        </Tabs>
                    </div>
                </div>
            )}
            {errors.length > 0 && (
                <div className='flex-1 flex flex-row justify-center items-center'>
                    <Text>{errors[0]}</Text>
                </div>
            )}
            {requestParams && !listDataIsPending && providers && selectedTab === 'list' && (
                <div className='flex flex-col min-h-40 w-full'>
                    {providers.length === 0 ? (
                    <div className='flex-1 flex flex-row justify-center items-center'>
                        <Text>No results found.</Text>
                    </div>
                    ) : (
                    <div className='grid grid-cols-1 sm:grid-cols-3 md:grid-cols-4 gap-x-5 gap-y-5 pt-5'>
                        {providers.map(item => <ProviderListCard key={item.id} item={item} setProviderId={setProviderId} />)}
                    </div>
                    )}
                </div>
            )}
            {requestParams && !listDataIsPending && markers && selectedTab === 'map' && (
                <div className='flex flex-col min-h-40 w-full'>
                    {markers.length === 0 ? (
                    <div className='flex-1 flex flex-row justify-center items-center'>No results found.</div>
                    ) : (
                    <div className='h-[70vh]'>
                        <APIProvider apiKey={import.meta.env.VITE_GOOGLE_MAPS_API_KEY}>
                            <Map
                                key={`${mapCenter}-${mapZoom}-${page}`}
                                style={{width: '100%', height: '100%'}}
                                defaultCenter={mapCenter}
                                defaultZoom={mapZoom}
                                gestureHandling={'greedy'}
                                disableDefaultUI={false}
                            >
                                {markers.map(item => (
                                    <Marker
                                        key={item.key}
                                        position={{
                                            lat:  item.lat,
                                            lng: item.lng
                                        }}
                                        clickable={true}
                                        onClick={() => { setSelectedMarker(item) }}
                                        title={item.name}
                                    />
                                ))}
                            </Map>
                        </APIProvider>
                    </div>
                    )}
                </div>
            )}
            {total > 0 && (
                <div className='w-full flex flex-col justify-center sm:flex-row sm:justify-between gap-3 mt-5'>
                    <div className='flex-1 flex flex-col justify-center items-end'>
                        <Pagination showControls total={total} initialPage={1} variant='light' page={page} onChange={setPage} />
                        <Text type='label'>Page number</Text>
                    </div>
                </div>
            )}
            <Modal isOpen={isOpen} onOpenChange={onOpenChange} className='twp'>
                <ModalContent>
                    {(onClose) => (
                        <>
                            <ModalHeader className="flex flex-col gap-1">Providers</ModalHeader>
                            <ModalBody className='pb-5'>
                                {selectedMarker?.items.map(item => <ProviderListCard item={item} setProviderId={setProviderId} />)}
                            </ModalBody>
                        </>
                    )}
                </ModalContent>
            </Modal>
        </div>
    );
};

const ProviderListCard = ({ item, setProviderId }) => {
    const name = [4,5,6,9].includes(item.providerTypeId) ? item.providerName : `${item.practitionerFirstName} ${item.practitionerLastName}, ${item.practitionerTitle}`;
    return (
        <div key={item.id} onClick={() => setProviderId(item.id)} className='shadow-sm group flex flex-col gap-3 justify-start sm:flex-col border border-gray-400 rounded-lg px-3 py-3 hover:border-primary cursor-pointer'>
            <div className='text-sm font-bold text-gray-500 uppercase leading-tight group-hover:text-secondary'>{name}</div>
            <div className='flex flex-col gap-2'>
                <div className='text-sm text-gray-600'><span className='font-medium'><Text>Provider ID Number</Text>:</span> {item.practitionerPCPID}</div>
                <div className='text-sm text-gray-600'></div>
            </div>
            <div className='flex flex-row gap-2 text-gray-600'>
                <div><MapPin size={16} /></div>
                <div className='flex flex-col text-sm text-gray-600'>
                    <div>{item?.providerAddress1}{item?.providerAddress2 ? `, ${item?.providerAddress2}` : ''}</div>
                    <div>{item?.providerCity}, {item?.providerState} {item?.providerZip}</div>
                </div>
            </div>
            <div className='flex flex-row gap-2 text-gray-600'>
                <div><Phone size={16} /></div>
                <div className='text-sm text-gray-600'>
                    <a href={`tel:+${item.providerPhone}`}>{formatPhoneNumber(item.providerPhone)}</a>
                </div>
            </div>
            <div className='flex flex-col gap-2'>
                <div className='text-sm text-gray-600 break-words'><span className='font-medium'><Text>Languages</Text></span>: <Text>{formatLanguages(item?.languages)}</Text></div>
            </div>
            <div className='flex flex-col gap-2'>
                <div className='text-sm text-gray-600'><span className='font-medium'><Text>Accepting New Patients</Text></span>: <span className='text-green-600 font-medium'>{item?.providerAcceptsNewPatient === 'Y' ? <Text>Yes</Text> : <Text>No</Text>}</span></div>
            </div>
            <div className='flex flex-col gap-2'>
                <div className='text-sm text-gray-600'><span className='font-medium'>{item?.providerSpecialty}</span></div>
            </div>
            <div className='flex-1 flex flex-row gap-2 justify-end items-end'>
                <a href='#' className='text-secondary text-sm font-medium' onClick={() => setProviderId(item.id)}><Text>More Details</Text> {`>>>`}</a>
            </div>
        </div>
    )
}

export default ProviderList;
